class SlideshowIndicators extends HTMLElement {
  constructor() {
    super();

    // Cache DOM queries
    this.slider = document.querySelector(this.dataset.targetEl);
    this.sliderList = this.slider.querySelector("ul");
    this.autoplay = this.slider.dataset.autoplay === "true" ? true : false;

    this.autoplayDelay = parseInt(this.slider.dataset.autoplayDelay)
      ? parseInt(this.slider.dataset.autoplayDelay) * 1000
      : 5000;

    if (!this.sliderList) {
      return;
    }

    // Cache slides
    this.slides = Array.from(this.sliderList.querySelectorAll("li"));

    this.observerOptions = {
      root: null,
      rootMargin: "0px",
      threshold: 0.5,
    };

    this.observer = new IntersectionObserver(
      this.handleIntersection.bind(this),
      this.observerOptions
    );

    // Throttle intersection observer callbacks
    this.throttledObserverCallback = this.throttle(this.handleIntersection, 100);

    this.slides.forEach((slide, index) => {
      this.observer.observe(slide);
      slide.dataset.slideIndex = index;
    });

    this.autoplayProgress = 0;
    this.autoplayAnimationFrameId = null;
    this.currentSlideIndex = 0;
    this.userInteraction = false;

    if (this.autoplay) {
      this.startAutoplay(this.autoplayDelay);
      this.slider.addEventListener("mouseenter", () => this.pauseAutoplay());
      this.slider.addEventListener("mouseleave", () => this.resumeAutoPlay());
    } else {
      this.stopAutoPlay();
    }

    let startX = 0;
    let isSwiping = false;

    this.slider.addEventListener(
      "mousedown",
      (event) => {
        startX = event.clientX;
        isSwiping = true;
        this.stopAutoPlay();
      },
      { passive: true }
    );

    // Debounce mousemove event
    this.slider.addEventListener(
      "mousemove",
      this.debounce((event) => {
        if (!isSwiping) return;
        const deltaX = event.clientX - startX;
        if (Math.abs(deltaX) > 20) {
          isSwiping = false;
          this.stopAutoPlay();
        }
      }, 50),
      { passive: true }
    );

    this.slider.addEventListener(
      "mouseup",
      () => {
        isSwiping = false;
      },
      { passive: true }
    );

    // Throttle wheel event
    this.slider.addEventListener(
      "wheel",
      this.throttle((event) => {
        if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
          this.stopAutoPlay();
        }
      }, 100),
      { passive: true }
    );
  }

  handleIntersection = (entries) => {
    entries.forEach((entry) => {
      const slideIndex = entry.target.dataset.slideIndex;

      if (entry.isIntersecting) {
        this.activateSlide(slideIndex);
      }
    });
  };

  activateSlide(slideIndex) {
    this.currentSlideIndex = parseInt(slideIndex, 10);

    const fill = this.querySelector(".slideshow-indicators__fill");
    if (fill) {
      const total = this.slides.length;
      if (this.classList.contains('slideshow-indicators--progress')) {
        fill.style.width = `${100 / total}%`;
        fill.style.transform = `translateX(${this.currentSlideIndex * 100}%)`;
      } else {
        fill.style.width = `${((this.currentSlideIndex + 1) / total) * 100}%`;
      }
    }

    const activeSlide = this.querySelector("ul > li.active");
    if (activeSlide) {
      activeSlide.classList.remove("active");
      activeSlide.style.setProperty("--delay-progress", `100%`);
    }

    const targetSlide = this.querySelectorAll(`ul > li`)[slideIndex];
    if (targetSlide) {
      targetSlide.classList.add("active");
      targetSlide.style.setProperty("--delay-progress", `0%`);
    }
  }

  startAutoplay(autoplayDelay) {
    this.autoplayProgress = 0;
    let startTime = performance.now();
    let lastUpdate = 0;
    const frameRate = 60; // 60 frames per second

    const animateAutoplay = () => {
      const currentTime = performance.now();
      const elapsedTime = currentTime - startTime;

      if (!this.userInteraction) {
        const delta = currentTime - lastUpdate;
        if (delta > (1000 / frameRate)) {
          lastUpdate = currentTime;
          this.logAutoplayProgress();

          this.autoplayProgress = (elapsedTime / autoplayDelay) * 100;

          if (this.autoplayProgress >= 100) {
            this.currentSlideIndex =
              (this.currentSlideIndex + 1) % this.slides.length;
            this.changeSlide(this.currentSlideIndex);
            this.autoplayProgress = 0;
            startTime = currentTime;
          }
        }
      }

      if (!this.userInteraction) {
        this.autoplayAnimationFrameId = requestAnimationFrame(animateAutoplay);
      }
    };

    if (!this.autoplayAnimationFrameId) {
      this.autoplayAnimationFrameId = requestAnimationFrame(animateAutoplay);
    }
  }

  stopAutoPlay() {
    this.userInteraction = true;
    this.slider.classList.add("no-autoplay");
    this.pauseAutoplay();
  }

  resumeAutoPlay() {
    if (!this.autoplayAnimationFrameId && !this.userInteraction) {
      this.startAutoplay(this.autoplayDelay);
    }
  }

  pauseAutoplay() {
    if (this.autoplayAnimationFrameId && !this.userInteraction) {
      cancelAnimationFrame(this.autoplayAnimationFrameId);
      this.autoplayAnimationFrameId = null;
    }
  }

  logAutoplayProgress() {
    const activeIndicator = this.slider.querySelector(".active");
    if (activeIndicator) {
      const currentDelayProgress = parseFloat(
        activeIndicator.style.getPropertyValue("--delay-progress")
      );

      const roundedProgress = Math.round(this.autoplayProgress);

      if (
        currentDelayProgress <= roundedProgress &&
        roundedProgress > 5
      ) {
        activeIndicator.style.setProperty(
          "--delay-progress",
          `${roundedProgress}%`
        );
      }
    }
  }

  changeSlide(slideIndex) {
    const targetSlide = this.slides[slideIndex];
    if (targetSlide) {
      const targetOffsetLeft = targetSlide.offsetLeft;
      this.sliderList.scroll({
        left: targetOffsetLeft,
        behavior: "smooth",
      });
    }
  }

  connectedCallback() {
    if (!this.autoplay) {
      this.slider.classList.add("no-autoplay");
    }

    const fill = this.querySelector(".slideshow-indicators__fill");
    if (fill) {
      fill.style.width = `${(1 / this.slides.length) * 100}%`;
    }

    const buttons = this.querySelectorAll("li button");

    buttons.forEach((button, index) => {
      button.addEventListener("click", () => {
        this.changeSlide(index);
        if (this.autoplayAnimationFrameId) {
          this.stopAutoPlay();
        }
      });
    });

    // Add event listeners for next and previous buttons
    const prevButton = this.slider.querySelector('.slider-button--prev');
    const nextButton = this.slider.querySelector('.slider-button--next');

    if (prevButton) {
      prevButton.addEventListener('click', this.showPreviousSlide.bind(this));
    }

    if (nextButton) {
      nextButton.addEventListener('click', this.showNextSlide.bind(this));
    }
  }

  showPreviousSlide() {
    const previousSlideIndex = (this.currentSlideIndex - 1 + this.slides.length) % this.slides.length;
    this.changeSlide(previousSlideIndex);
  }

  showNextSlide() {
    const nextSlideIndex = (this.currentSlideIndex + 1) % this.slides.length;
    this.changeSlide(nextSlideIndex);
  }

  // Throttle function
  throttle(fn, wait) {
    let inThrottle, lastFn, lastTime;
    return function () {
      const context = this,
        args = arguments;
      if (!inThrottle) {
        fn.apply(context, args);
        lastTime = Date.now();
        inThrottle = true;
      } else {
        clearTimeout(lastFn);
        lastFn = setTimeout(function () {
          if (Date.now() - lastTime >= wait) {
            fn.apply(context, args);
            lastTime = Date.now();
          }
        }, Math.max(wait - (Date.now() - lastTime), 0));
      }
    };
  }

  // Debounce function
  debounce(fn, delay) {
    let timeoutId;
    return function () {
      const context = this,
        args = arguments;
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => fn.apply(context, args), delay);
    };
  }
}

customElements.define("slideshow-indicators", SlideshowIndicators);